import type React from 'react'
import { useMemo, useRef } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { Navigate, useParams } from '@tanstack/react-router'

import { useFirebaseUser } from '@/auth/useFirebaseUser.ts'
import { GlobalAssignmentDetailsView } from '@/components/templates/GlobalAssignmentDetailsView'
import { RegionAssignmentDetailsView } from '@/components/templates/RegionAssignmentDetailsView'
import { AppPageMapContext } from '@/pages/dashboard/AppPageMapProvider'
import { assignmentQuery } from '@/state/assignments/assignments'

import { useGlobalAssignment } from './useGlobalAssignment'
import { useRegionAssignment } from './useRegionAssigment'

export const AssignmentDetailsPage: React.FC = () => {
  const user = useFirebaseUser()
  const { assignmentId } = useParams({ from: '/assignments/$assignmentId' })

  // First, load the basic assignment to determine its type
  const { data: assignmentSnapshot } = useSuspenseQuery(assignmentQuery(assignmentId))
  const assignmentType = assignmentSnapshot.data?.type ?? 'region'

  if (!user?.sys_admin || !assignmentId) {
    return <Navigate to="/" search={{ central_id: undefined }} replace />
  }

  // Render the appropriate view based on assignment type
  if (assignmentType === 'global') {
    return <GlobalAssignmentDetailsPageContent />
  }

  return <RegionAssignmentDetailsPageContent />
}

const RegionAssignmentDetailsPageContent: React.FC = () => {
  const assignment = useRegionAssignment()
  const mapRef = useRef<google.maps.Map | undefined>(undefined)

  const mapContextValue = useMemo(
    () => ({
      onMapLoad: (map: google.maps.Map) => {
        mapRef.current = map
      },
      onMapUnmount: () => {
        mapRef.current = undefined
      },
      // No-op: assignment details page doesn't need map focus features
      focusTransportRequest() {},
      focusRegion() {},
      refresh() {},
    }),
    []
  )

  if (!assignment) {
    return <div>Loading...</div>
  }

  return (
    <AppPageMapContext.Provider value={mapContextValue}>
      <RegionAssignmentDetailsView assignment={assignment} />
    </AppPageMapContext.Provider>
  )
}

const GlobalAssignmentDetailsPageContent: React.FC = () => {
  const assignment = useGlobalAssignment()
  const mapRef = useRef<google.maps.Map | undefined>(undefined)

  const mapContextValue = useMemo(
    () => ({
      onMapLoad: (map: google.maps.Map) => {
        mapRef.current = map
      },
      onMapUnmount: () => {
        mapRef.current = undefined
      },
      // No-op: assignment details page doesn't need map focus features
      focusTransportRequest() {},
      focusRegion() {},
      refresh() {},
    }),
    []
  )

  if (!assignment) {
    return <div>Loading...</div>
  }

  return (
    <AppPageMapContext.Provider value={mapContextValue}>
      <GlobalAssignmentDetailsView assignment={assignment} />
    </AppPageMapContext.Provider>
  )
}
